import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Trophy, DollarSign, GaugeCircle, Clock, TrendingUp, Users, XCircle, Bell } from "lucide-react";
import { sr } from "../../lib/sr";

const money = (n) => (n == null ? "—" : `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`);
const pct = (r) => `${Math.round((r || 0) * 100)}%`;
const STAGE_LABEL = {
  draft: "Draft", ready_for_review: "Ready for review", shared: "Shared",
  negotiating: "Negotiating", accepted: "Accepted", declined: "Declined", expired: "Expired",
};

function Kpi({ label, value, Icon }) {
  return (
    <div className="sr-card" style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <Icon size={16} style={{ color: "var(--gold)" }} />
      <div className="sr-muted" style={{ fontSize: 12 }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 800, color: "var(--navy)" }}>{value}</div>
    </div>
  );
}

export default function ProposalAnalytics() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    sr.proposalAnalytics().then(setD).catch((e) => setErr(e?.response?.data?.detail || "Couldn't load analytics"));
  }, []);

  if (err) return <div className="sr-card" style={{ padding: 24, color: "#b42318" }} data-testid="analytics-error">{err}</div>;
  if (!d) return <div style={{ textAlign: "center", padding: 50 }}><Loader2 className="sr-spin" size={24} /></div>;

  const t = d.totals || {};
  const openStages = ["draft", "ready_for_review", "shared", "negotiating"];
  const maxStageVal = Math.max(1, ...openStages.map((s) => d.stage_value?.[s] || 0));

  return (
    <div data-testid="proposal-analytics">
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))", gap: 12 }} data-testid="analytics-kpis">
        <Kpi label="Win rate" value={pct(t.win_rate)} Icon={Trophy} />
        <Kpi label="Avg deal size" value={money(t.avg_deal_size)} Icon={DollarSign} />
        <Kpi label="Open pipeline" value={money(t.pipeline_value)} Icon={GaugeCircle} />
        <Kpi label="Won value" value={money(t.won_value)} Icon={TrendingUp} />
        <Kpi label="Avg sales cycle" value={t.avg_sales_cycle_days != null ? `${t.avg_sales_cycle_days} d` : "—"} Icon={Clock} />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginTop: 16 }}>
        <div className="sr-card">
          <div style={{ fontWeight: 700, marginBottom: 10 }}>Open pipeline by stage</div>
          {openStages.every((s) => !(d.stage_value?.[s])) ? (
            <div className="sr-muted" style={{ fontSize: 13 }}>No open proposals with a priced plan yet.</div>
          ) : openStages.map((s) => {
            const v = d.stage_value?.[s] || 0;
            return (
              <div key={s} style={{ marginBottom: 8 }} data-testid={`pipeline-${s}`}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12.5, marginBottom: 3 }}>
                  <span>{STAGE_LABEL[s]} <span className="sr-muted">· {d.by_stage?.[s] || 0}</span></span>
                  <strong>{money(v)}</strong>
                </div>
                <div style={{ height: 8, background: "#EEF2F7", borderRadius: 999 }}>
                  <div style={{ width: `${(v / maxStageVal) * 100}%`, height: "100%", background: "var(--gold)", borderRadius: 999 }} />
                </div>
              </div>
            );
          })}
        </div>

        <div className="sr-card">
          <div style={{ fontWeight: 700, marginBottom: 10 }}>Avg time in stage</div>
          {Object.keys(d.avg_days_in_stage || {}).length === 0 ? (
            <div className="sr-muted" style={{ fontSize: 13 }}>Not enough stage history yet — advance a few proposals to see pacing.</div>
          ) : (
            <div style={{ display: "grid", gap: 6 }}>
              {Object.entries(d.avg_days_in_stage).map(([s, days]) => (
                <div key={s} style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }} data-testid={`timeinstage-${s}`}>
                  <span className="sr-muted">{STAGE_LABEL[s] || s}</span>
                  <strong>{days} {days === 1 ? "day" : "days"}</strong>
                </div>
              ))}
            </div>
          )}
          <div className="sr-muted" style={{ fontSize: 11, marginTop: 10 }}>
            {t.won}/{t.total} won · {t.lost} lost · {t.open} open
          </div>
        </div>
      </div>

      {d.scope === "all" && d.leaderboard && (
        <div className="sr-card" style={{ marginTop: 14, padding: 0, overflow: "hidden" }} data-testid="analytics-leaderboard">
          <div style={{ fontWeight: 700, padding: "12px 16px", display: "flex", alignItems: "center", gap: 8 }}><Users size={16} /> Salesperson leaderboard</div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead><tr style={{ background: "#F8FAFC", textAlign: "left" }}>
              <th style={{ padding: "8px 16px" }}>Salesperson</th>
              <th style={{ padding: "8px 16px", textAlign: "right" }}>Proposals</th>
              <th style={{ padding: "8px 16px", textAlign: "right" }}>Won</th>
              <th style={{ padding: "8px 16px", textAlign: "right" }}>Win rate</th>
              <th style={{ padding: "8px 16px", textAlign: "right" }}>Won value</th>
              <th style={{ padding: "8px 16px", textAlign: "right" }}>Pipeline</th>
            </tr></thead>
            <tbody>
              {d.leaderboard.map((r) => (
                <tr key={r.salesperson_id} style={{ borderTop: "1px solid var(--border)" }} data-testid={`leader-${r.salesperson_id}`}>
                  <td style={{ padding: "8px 16px", fontWeight: 600, color: "var(--navy)" }}>{r.name}</td>
                  <td style={{ padding: "8px 16px", textAlign: "right" }}>{r.total}</td>
                  <td style={{ padding: "8px 16px", textAlign: "right" }}>{r.won}</td>
                  <td style={{ padding: "8px 16px", textAlign: "right" }}>{pct(r.win_rate)}</td>
                  <td style={{ padding: "8px 16px", textAlign: "right", fontWeight: 700 }}>{money(r.won_value)}</td>
                  <td style={{ padding: "8px 16px", textAlign: "right" }}>{money(r.pipeline_value)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: d.scope === "all" ? "1fr 1fr" : "1fr", gap: 14, marginTop: 14 }}>
        <div className="sr-card" data-testid="lost-reasons">
          <div style={{ fontWeight: 700, marginBottom: 10, display: "flex", alignItems: "center", gap: 8 }}><XCircle size={16} style={{ color: "#B42318" }} /> Why we lose</div>
          {(d.lost_reasons || []).length === 0 ? (
            <div className="sr-muted" style={{ fontSize: 13 }}>No declined or expired proposals yet — nice.</div>
          ) : (
            <div style={{ display: "grid", gap: 8 }}>
              {(() => { const mx = Math.max(1, ...d.lost_reasons.map((r) => r.count)); return d.lost_reasons.map((r) => (
                <div key={r.reason} data-testid={`lostreason-${r.reason}`}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12.5, marginBottom: 3 }}>
                    <span>{r.label}</span><strong>{r.count}</strong>
                  </div>
                  <div style={{ height: 8, background: "#FBE9E7", borderRadius: 999 }}>
                    <div style={{ width: `${(r.count / mx) * 100}%`, height: "100%", background: "#E2574C", borderRadius: 999 }} />
                  </div>
                </div>
              )); })()}
            </div>
          )}
        </div>
        {d.scope === "all" ? <ReminderSettings /> : null}
      </div>
    </div>
  );
}

function ReminderSettings() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { sr.proposalReminderSettings().then(setCfg).catch(() => {}); }, []);
  const save = async (patch) => {
    setSaving(true);
    try { const r = await sr.proposalReminderSettingsSave(patch); setCfg((c) => ({ ...c, ...r })); toast.success("Reminder settings saved"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save"); } finally { setSaving(false); }
  };
  if (!cfg) return <div className="sr-card" style={{ textAlign: "center", padding: 24 }}><Loader2 className="sr-spin" size={18} /></div>;
  const num = { width: 70, padding: "7px 9px", border: "1px solid var(--border)", borderRadius: 8, fontSize: 13 };
  return (
    <div className="sr-card" data-testid="reminder-settings">
      <div style={{ fontWeight: 700, marginBottom: 4, display: "flex", alignItems: "center", gap: 8 }}><Bell size={16} style={{ color: "var(--gold)" }} /> Expiry reminders</div>
      <div className="sr-muted" style={{ fontSize: 11.5, marginBottom: 12 }}>A daily nudge to the salesperson before a shared proposal lapses.</div>
      <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, marginBottom: 10 }}>
        <input type="checkbox" checked={!!cfg.enabled} disabled={saving} onChange={(e) => save({ enabled: e.target.checked })} data-testid="rem-enabled" /> Reminders enabled
      </label>
      <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, marginBottom: 12 }}>
        <input type="checkbox" checked={!!cfg.auto_expire} disabled={saving} onChange={(e) => save({ auto_expire: e.target.checked })} data-testid="rem-autoexpire" /> Auto-expire overdue proposals
      </label>
      <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
        <label style={{ fontSize: 12, fontWeight: 600 }}>Validity (days)
          <input type="number" min={1} max={180} defaultValue={cfg.validity_days} disabled={saving} style={{ ...num, display: "block", marginTop: 4 }}
            data-testid="rem-validity" onBlur={(e) => { const v = parseInt(e.target.value, 10); if (v && v !== cfg.validity_days) save({ validity_days: v }); }} />
        </label>
        <label style={{ fontSize: 12, fontWeight: 600 }}>Lead time (days)
          <input type="number" min={0} max={60} defaultValue={cfg.lead_days} disabled={saving} style={{ ...num, display: "block", marginTop: 4 }}
            data-testid="rem-lead" onBlur={(e) => { const v = parseInt(e.target.value, 10); if (!isNaN(v) && v !== cfg.lead_days) save({ lead_days: v }); }} />
        </label>
      </div>
      {cfg.last_run_date ? <div className="sr-muted" style={{ fontSize: 11, marginTop: 10 }}>Last run {cfg.last_run_date}{cfg.last_result ? ` · ${cfg.last_result.reminded || 0} reminded, ${cfg.last_result.expired || 0} expired` : ""}</div> : null}
    </div>
  );
}
